import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import axios from 'axios';

import OverviewDashboard from './dashboards/OverviewDashboard';
import IndividualProfileDashboard from './dashboards/IndividualProfileDashboard';
import TrendsDashboard from './dashboards/TrendsDashboard';

import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import PasswordResetPage from './pages/Auth/PasswordResetPage';

import { AuthProvider, useAuth } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// Configuração das URLs da API
const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1', // Usar variável de ambiente com fallback
  endpoints: {
    origem: '/origem',
    pessoas: '/pessoas',
    nineBox: '/nine-box',
    radarCompetencias: '/radar-competencias',
    heatmapCompetencias: '/heatmap-competencias',
    distribuicaoScarf: '/distribuicao-scarf',
    alertasCriticos: '/alertas-criticos',
    competenciasHistorico: '/competencias-historico',
  }
};

const AppContent = () => {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard1'); // Manter para navegação interna dos dashboards

  // Função genérica para fazer chamadas API
  const apiCall = useCallback(async (endpoint, params = {}) => {
    try {
      const url = new URL(`${API_CONFIG.baseUrl}${endpoint}`);
      Object.keys(params).forEach(key => {
        if (params[key]) url.searchParams.append(key, params[key]);
      });

      const response = await axios.get(url.toString());
      return response.data;
    } catch (error) {
      console.error(`Erro ao chamar API ${endpoint}:`, error);
      return [];
    }
  }, []);

  if (loading) {
    return <div>Carregando aplicação...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Ediand</h1>
        {user && (
          <div>
            <span className="mr-4">Bem-vindo, {user.usu_nm_primeiro || user.usu_ds_email}!</span>
            <button
              onClick={logout}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            >
              Logout
            </button>
          </div>
        )}
      </header>

      {/* Navigation Tabs */}
      {user && ( // Only show navigation tabs if user is logged in
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-8">
              <Link
                to="/dashboard/overview"
                onClick={() => setActiveTab('dashboard1')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard1'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Visão Geral
              </Link>
              <Link
                to="/dashboard/individual"
                onClick={() => setActiveTab('dashboard2')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard2'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Perfil Individual
              </Link>
              <Link
                to="/dashboard/trends"
                onClick={() => setActiveTab('dashboard3')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard3'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Tendências
              </Link>
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<PasswordResetPage />} />
          <Route path="/" element={user ? <Navigate to="/dashboard/overview" replace /> : <Navigate to="/login" replace />} />

          <Route
            path="/dashboard/overview"
            element={
              <PrivateRoute>
                <OverviewDashboard apiCall={apiCall} activeTab={activeTab} />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/individual"
            element={
              <PrivateRoute>
                <IndividualProfileDashboard apiCall={apiCall} activeTab={activeTab} />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/trends"
            element={
              <PrivateRoute>
                <TrendsDashboard apiCall={apiCall} activeTab={activeTab} />
              </PrivateRoute>
            }
          />

          {/* Adicione outras rotas protegidas aqui */}
        </Routes>
      </main>
    </div>
  );
};


function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;